// Sign-In Link Landing page — user clicks link in email, lands here.
// Same-browser policy: the mfaSessionToken is only in the localStorage of the browser
// where they originally submitted their email. If missing, we refuse the request with
// a clear error. On success, tokens are stored and user is redirected to dashboard.
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Row, Col, Result } from 'antd';
import Lottie from 'lottie-react';
import { ThemeButton } from '../Styles/Theme';
import { LoginWrapper } from './LoginStyle';
import LottieLoader from '../../Components/common/LottieLoader';
import useLottieData from '../../hooks/useLottieData';
import { consumeMagicLinkApi, loginReset } from '../../store/actions/loginAction';
import { setPermissions } from '../../store/actions/permissionsAction';
import { STORAGE_KEYS } from '../../utils/constants';
import { checkModulePermissions, getFirstAvailableRoute, isLogin } from '../../utils/helpers';

const SignInLinkLanding = (props) => {
	const { loginState, consume, loginReset, setPermissions } = props;
	const { token } = useParams();
	const navigate = useNavigate();
	const [status, setStatus] = useState('loading');
	const [errorMsg, setErrorMsg] = useState('');
	// 'wrong-browser' → mfaSessionToken missing (no one logged in OR someone else is)
	// 'generic'       → backend error after consume attempt
	const [errorKind, setErrorKind] = useState('generic');
	// Fall back to static PNG if the Lottie animation fails to load.
	const loginLottie = useLottieData('/lottie/login.json');
	const [lottieFailed, setLottieFailed] = useState(false);

	useEffect(() => {
		if (!token) {
			setStatus('error');
			setErrorKind('generic');
			setErrorMsg('Missing token in link.');
			return;
		}

		// Case: user already consumed THIS exact token in this browser (back-button scenario).
		// They're signed in, mfaSessionToken has been cleaned up, but they pressed Back.
		// Just send them home.
		const lastConsumed = localStorage.getItem('mfaLastConsumedToken');
		if (lastConsumed === token && isLogin()) {
			navigate('/', { replace: true });
			return;
		}

		const mfaSessionToken = localStorage.getItem('mfaSessionToken');

		// Same-browser policy: mfaSessionToken only exists in the browser that initiated login.
		// If it's missing, either nobody is signed in here, OR a different user is signed in here
		// (either way, this is the wrong browser for this link). Single clear message.
		if (!mfaSessionToken) {
			setStatus('error');
			setErrorKind('wrong-browser');
			setErrorMsg('');
			return;
		}

		// Has mfaSessionToken → proceed to consume.
		// If someone else is also logged in here (rare — same browser started a second sign-in),
		// backend's response will overwrite localStorage with the new user's tokens.
		consume({ token, mfaSessionToken });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	useEffect(() => {
		if (loginState.apiState === 'success') {
			if (loginState.data) {
				localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, loginState.data.accessToken);
				localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, loginState.data.refreshToken);
				localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(loginState.data.user));
				if (loginState.data.permissions) {
					localStorage.setItem(STORAGE_KEYS.PERMISSIONS, JSON.stringify(loginState.data.permissions));
					setPermissions(loginState.data.permissions);
				}
				if (loginState.data.appVersion) {
					localStorage.setItem('app_version', loginState.data.appVersion);
				}
			}
			// Remember this token was consumed in this browser — so if user presses Back
			// after landing on dashboard, we recognise it and navigate home instead of
			// showing the "wrong browser" error.
			if (token) {
				localStorage.setItem('mfaLastConsumedToken', token);
			}
			// Clean up transient MFA keys
			localStorage.removeItem('mfaSessionToken');
			localStorage.removeItem('mfaMaskedEmail');
			localStorage.removeItem('mfaExpiryMinutes');
			loginReset();
			// Per-org login redirect: backend sets redirect_chat_id when the
			// user's org has chat_redirection_enabled. Take that path before
			// the normal landing-page fallback so the user lands directly in
			// their fresh "New Insights" chat.
			if (loginState.data?.redirect_chat_id) {
				navigate(`/insight-engine/${loginState.data.redirect_chat_id}`);
				return;
			}
			const dashboardPermission = checkModulePermissions('dashboard');
			if (dashboardPermission.authorized) {
				navigate('/', { replace: true });
			} else {
				const first = getFirstAvailableRoute();
				navigate(first ? first.path : '/', { replace: true });
			}
		} else if (loginState.apiState === 'error') {
			const code = loginState.data?.data?.code;
			let friendlyMessage;
			if (code === 'MFA_LINK_EXPIRED') {
				friendlyMessage = 'This sign-in link has expired. Please sign in again to receive a fresh one.';
			} else if (code === 'MFA_LINK_INVALIDATED' || code === 'MFA_SESSION_INVALID') {
				friendlyMessage = 'This link is no longer valid. Whenever you tap "Resend link", older emails stop working - please open the link from the most recent email, or sign in again.';
			} else {
				// Never show raw error codes to the user
				friendlyMessage = 'This sign-in link is invalid or has already been used. Please sign in again.';
			}
			setStatus('error');
			setErrorMsg(friendlyMessage);
			loginReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginState]);

	if (status === 'error') {
		// Wrong-browser (covers both "nobody logged in" and "different user logged in here")
		if (errorKind === 'wrong-browser') {
			return (
				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						minHeight: '100vh',
						padding: '20px',
						backgroundColor: '#f5f5f5',
					}}
				>
					<Result
						status='warning'
						title='Open this link in the right browser'
						subTitle={
							<div style={{ maxWidth: 520, margin: '0 auto', lineHeight: 1.6 }}>
								Please open this sign-in link in the <strong>same browser</strong> where
								you are trying to sign in.
							</div>
						}
					/>
				</div>
			);
		}

		// Generic error (backend rejection — expired, invalidated, etc.)
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					minHeight: '100vh',
					padding: '20px',
					backgroundColor: '#f5f5f5',
				}}
			>
				<Result
					status='error'
					title='Sign-in link unavailable'
					subTitle={errorMsg}
					extra={
						<ThemeButton size='large' className='solidPurple' onClick={() => navigate('/login', { replace: true })}>
							Back to sign in
						</ThemeButton>
					}
				/>
			</div>
		);
	}

	return (
		<LoginWrapper>
			<Row gutter={[60, 16]}>
				<Col xs={24} sm={24} md={12} lg={10} xl={10}>
					<div className='loginLogo'>
						<img src='/images/crowdvox-logo-purple.svg' alt='CrowdVox' />
					</div>
					<div className='loginContainer'>
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
							<LottieLoader inline size='medium' />
							<h1 style={{ marginTop: 16, fontSize: 22 }}>Signing you in...</h1>
							<p style={{ color: '#6D6A67', fontSize: 14, marginTop: 8 }}>Please wait a moment.</p>
						</div>
					</div>
				</Col>
				<Col xs={24} sm={24} md={12} lg={14} xl={14}>
					<div className='loginBannerCircle'>
						<div className='circle'>
							{lottieFailed || !loginLottie ? (
								<img src='/images/login-circle.png' alt='' />
							) : (
								<Lottie
									animationData={loginLottie}
									loop
									autoplay
									style={{ width: '100%', height: '100%' }}
									onError={() => setLottieFailed(true)}
								/>
							)}
						</div>
					</div>
				</Col>
			</Row>
		</LoginWrapper>
	);
};

const mapStateToProps = (state) => ({ loginState: state.login });
const mapDispatchToProps = (dispatch) => ({
	consume: (params) => dispatch(consumeMagicLinkApi(params)),
	loginReset: () => dispatch(loginReset()),
	setPermissions: (params) => dispatch(setPermissions(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignInLinkLanding);
